import { defineMessages, type IntlShape } from 'react-intl';
import type { BlockConfigBase, Brain, JSONSchema } from '@plone/types';
import { addAlignmentStyle } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/styles';
import {
  addCtaFieldset,
  type CtaBlockExtender,
} from '@redturtle/qubicaamf-theme/components/Blocks/extenders/cta';
import addBlocksBg from '../extenders/addBlocksBg';
import addBlocksFullWidth from '../extenders/addBlocksFullWidth';
import addBlockLines from '../extenders/addBlockLines';
import type { Testimonial } from '../../View/NewsTestimonialsView/NewsTestimonialView';

export interface TestimonialsData extends CtaBlockExtender, JSONSchema {
  '@type': 'testimonials';
  testimonials?: Array<{
    '@id': string;
    testimonial: Omit<Testimonial, 'referenced_product'> & {
      referenced_product_info: Brain;
    };
  }>;
  text?: object;
}

export const TestimonialsSchema = ({
  data,
  intl,
}: {
  data: TestimonialsData;
  intl: IntlShape;
}): JSONSchema => {
  const schema = {
    title: intl.formatMessage(messages.title),
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        fields: ['title', 'testimonials'],
      },
    ],
    properties: {
      testimonials: {
        title: intl.formatMessage(messages.testimonials_title),
        widget: 'object_list',
        schema: {
          title: intl.formatMessage(messages.testimonial_title),
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['testimonial'],
            },
          ],
          properties: {
            testimonial: {
              title: intl.formatMessage(messages.testimonial_title),
              widget: 'object_browser',
              mode: 'link',
              return: 'single',
            },
          },
          required: ['testimonial'],
        },
      },
      title: {
        title: intl.formatMessage(messages.title_title),
      },
    },
    required: ['title'],
  };

  addCtaFieldset({ schema, intl });
  addAlignmentStyle({
    schema,
    intl,
    formData: data,
    alignments: ['left', 'right'],
  });
  addBlocksBg({ schema, formData: data, intl });
  addBlocksFullWidth({ schema, formData: data, intl });
  addBlockLines({ schema, formData: data, intl });

  return schema;
};

const messages = defineMessages({
  title: {
    id: 'redturtle__volto-blocks__testimonials_title',
    defaultMessage: 'Testimonials',
  },
  testimonials_title: {
    id: 'redturtle__volto-blocks__testimonials_testimonials_title',
    defaultMessage: 'Testimonials',
  },
  testimonial_title: {
    id: 'redturtle__volto-blocks__testimonials_testimonial_title',
    defaultMessage: 'Testimonial',
  },
  title_title: {
    id: 'Title',
    defaultMessage: 'Title',
  },
  text_title: {
    id: 'Text',
    defaultMessage: 'Text',
  },
});

export interface TestimonialsConfig
  extends Omit<BlockConfigBase, 'blockSchema'> {
  blockSchema: typeof TestimonialsSchema;
}
